import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';

@Injectable({
  providedIn: 'root',
})
export class CardsService {
  /**
   * Calculate the number of cards to show based on aspect ratio
   * @param aspectRatio The aspect ratio of the cards
   * @returns Number of cards to display
   */
  getNoOfCardsShown(aspectRatio: string): number {
    return aspectRatio === '2x3'
      ? 7
      : aspectRatio === '9x16'
      ? 6
      : aspectRatio === '16x9'
      ? 5
      : 7;
  }

  /**
   * Calculate the gap between cards in viewport units
   * @param gap The gap value in pixels
   * @returns Gap value in viewport units
   */
  getGapInVw(gap: number): string {
    return `${gap}vw`;
  }

  /**
   * Get the appropriate title for a card
   * @param resource The resource object containing titles
   * @returns The appropriate title
   */
  getCardTitle(resource: ResourceVm): string {
    return resource.title || resource.name || '';
  }

  /**
   * Get the appropriate subtitle for a card
   * @param resource The resource object containing subtitles
   * @returns The appropriate subtitle
   */
  getCardSubtitle(resource: ResourceVm): string {
    return resource.subtitle || resource?.shortDescription || '';
  }

  /**
   * Check if a card is premium content
   * @param resource The resource object
   * @returns Boolean indicating if the content is premium
   */
  isPremiumContent(resource: ResourceVm): boolean {
    return resource.isPremium || false;
  }

  calculateCardWidth(
    parentWidth: number,
    gap: number,
    numberOfCards: number,
    padding: number,
    borderThickness: number,
    isHalfVisible?: boolean
  ): { sectionWidth: number; cardWidth: number } {
    let halfVisibleWidth = 0;
    let calcParentWidth = parentWidth;
    // TODO : give margin in dynamic way
    if (isHalfVisible) {
      calcParentWidth = parentWidth - this.pxConversion(6.4);
    }

    if (isHalfVisible) {
      halfVisibleWidth = this.vwConversionNumber(numberOfCards > 5 ? 200 : 500);
    }

    const sectionWidth =
      (calcParentWidth - gap * (numberOfCards - 1)) / numberOfCards -
      halfVisibleWidth;

    const cardWidth =
      sectionWidth -
      this.pxConversion(padding) -
      this.pxConversion(borderThickness);
    return {
      sectionWidth,
      cardWidth,
    };
  }

  calculateCardHeight(cardWidth: number, aspectRatio: string) {
    let cardHeight: number;

    switch (aspectRatio) {
      case '16x9':
        cardHeight = cardWidth * 0.5625; // 9/16 = 0.5625
        break;
      case '2x3':
        cardHeight = cardWidth * 1.5; // 3/2 = 1.5
        break;
      case '1x1':
        cardHeight = cardWidth * 1; // 1/1 = 1
        break;
      case '9x16':
        cardHeight = cardWidth * 1.7777777777777777; // 16/9 = 1.7777777777777777
        break;
      default:
        cardHeight = cardWidth * 0.5625; // Default to 16x9
        break;
    }

    return cardHeight;
  }

  vwConversionNumber(px: number): number {
    const winnerWidth = window.innerWidth;
    return (px / winnerWidth) * 100;
  }

  vwConversion(px: number): string {
    return `${this.vwConversionNumber(px)}vw`;
  }

  pxConversion(vw: number): number {
    const winnerWidth = window.innerWidth;
    return (vw / 100) * winnerWidth;
  }
}
